import React, { useState } from 'react';
import Layout from '../../Components/Layout';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Forum as ForumIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getConversationList } from '../../redux/actions/conversationsActions';
import ConversationDetailSidebar from './components/ConversationDetailSidebar';
import useCallOnce from '../../hooks/useCallOnce';
import { getWebhookData } from '../../redux/actions/billingActions';

function Conversation({ selectedUserId }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    conversations,
    loading,
    pagination,
    summary
  } = useSelector((state) => state.conversations);
  const { webhookData } = useSelector((state) => state.billing);

  // Local state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    call_summary_title: null,
    call_successful: '',
    type: '',
    searchKeyword: '',
    customerEmail: ''
  });

  // Load conversations on component mount - use useCallOnce to prevent double API calls
  useCallOnce('conversations-initial-load', () => {
    dispatch(getConversationList(rowsPerPage, 1, selectedUserId, filters));
  }, []);

  useCallOnce('billing/getWebhookData', () => {
    dispatch(getWebhookData());
  });

  const webhookPerCredit = webhookData?.getCosting[0]?.percredit || 0

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getConversationList(rowsPerPage, newPage + 1, selectedUserId, filters));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(getConversationList(newRowsPerPage, 1, selectedUserId, filters));
  };

  // Handle refresh button click - reset mode
  const handleRefresh = () => {
    setPage(0);
    dispatch(getWebhookData());
    dispatch(getConversationList(rowsPerPage, 1, selectedUserId, filters));
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle filter search
  const handleFilterSearch = () => {
    setPage(0);
    dispatch(getWebhookData());
    dispatch(getConversationList(rowsPerPage, 1, selectedUserId, filters));
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const resetFilters = {
      fromDate: null,
      toDate: null,
      call_summary_title: null,
      call_successful: '',
      type: '',
      searchKeyword: '',
      customerEmail: ''
    };
    setFilters(resetFilters);
    setPage(0);
    dispatch(getWebhookData());
    dispatch(getConversationList(rowsPerPage, 1, selectedUserId, resetFilters));
  };

  // Handle row click to open sidebar
  const handleRowClick = (conversationId) => {
    setSelectedConversationId(conversationId);
    setSidebarOpen(true);
  };

  // Handle sidebar close
  const handleSidebarClose = () => {
    setSidebarOpen(false);
    setSelectedConversationId(null);
  };

  // Handle card expansion for mobile
  const handleCardExpand = (conversationId) => {
    setExpandedCards(prev => ({
      ...prev,
      [conversationId]: !prev[conversationId]
    }));
  };

  // Format date from ISO string or Unix timestamp in America/New_York (EST/EDT)
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    let date;
    if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = new Date(dateValue * 1000);
    }

    // Always present in US Eastern time (shows EST/EDT automatically)
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    }).format(date);
  };

  // Format duration from seconds
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get status color for chip
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'unknown':
        return 'default';
      case 'failure':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helpers to render date as MM/DD/YYYY in TextField while storing ISO (YYYY-MM-DD)
  const formatDateForUi = (isoDateString) => {
    if (!isoDateString) return '';
    const [year, month, day] = isoDateString.split('-');
    if (!year || !month || !day) return '';
    return `${month}/${day}/${year}`;
  };

  // UI Date Field: shows MM/DD/YYYY when blurred, switches to native picker on focus
  const UiDateField = ({ label, value, onChange, min, max }) => {
    const hiddenDateRef = React.useRef(null);

    const openPicker = () => {
      const el = hiddenDateRef.current;
      if (!el) return;
      try { el.focus(); } catch (_) { }
      if (typeof el.showPicker === 'function') {
        try { el.showPicker(); } catch (_) { }
      } else {
        try { el.click(); } catch (_) { }
      }
    };

    return (
      <Box
        sx={{ position: 'relative' }}
        onMouseDown={(e) => { e.preventDefault(); openPicker(); }}
      >
        {/* Visible text field showing MM/DD/YYYY */}
        <TextField
          fullWidth
          label={label}
          type="text"
          placeholder={'MM/DD/YYYY'}
          value={formatDateForUi(value || '')}
          onFocus={openPicker}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
          // Do not allow typing; picker controls the value
          inputProps={{ readOnly: true }}
        />

        {/* Hidden native date input used to invoke the picker */}
        <input
          ref={hiddenDateRef}
          type="date"
          value={value || ''}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value || null)}
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 10,
            pointerEvents: 'auto',
          }}
        />
      </Box>
    );
  };

  // Mobile Card Component
  const MobileConversationCard = ({ conversation, index }) => {
    const isExpanded = expandedCards[conversation.conversation_id];

    return (
      <Card
        sx={{
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 2
          }
        }}
        onClick={() => handleRowClick(conversation.conversation_id)}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                {conversation?.convoId || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                {formatDate(conversation?.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={conversation?.analysis?.call_successful}
                color={getStatusColor(conversation?.analysis?.call_successful)}
                size="small"
                variant="outlined"
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardExpand(conversation.conversation_id);
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Duration
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDuration(conversation.call_duration_secs || conversation.metadata?.call_duration_secs)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Messages
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {conversation.msg || conversation.message_count || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Total Cost
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {`$${((webhookPerCredit * (conversation?.metadata?.charging?.call_charge)) + (conversation?.metadata?.charging?.llm_price)).toFixed(3)}`}
              </Typography>
            </Box>
          </Stack>

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                Details
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>Call Credits:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {conversation?.metadata?.charging?.call_charge || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>LLM Credits:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {conversation?.metadata?.charging?.llm_charge || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>Call Cost:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {`$${(webhookPerCredit * (conversation?.metadata?.charging?.call_charge)).toFixed(3)}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>LLM Cost:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {`$${(conversation?.metadata?.charging?.llm_price || 0).toFixed(3)}`}
                  </Typography>
                </Box>
                {conversation.call_summary_title && (
                  <Box>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>Title:</Typography>
                    <Typography variant="body2" sx={{ color: '#374151' }}>
                      {conversation.call_summary_title || conversation?.analysis?.call_summary_title || '-'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* <Layout> */}
      <Box sx={{ p: { xs: 2, sm: 4 }, width: '100%', minWidth: 0, mt: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Conversations
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              View your AI agent conversations
            </Typography>
          </Box>
          {/* <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                borderColor: '#6b7280',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#374151',
                  backgroundColor: '#f9fafb',
                }
              }}
            >
              Refresh
            </Button>
          </Box> */}
        </Box>

        {/* Filter Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* From Date */}
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <UiDateField
                label="From Date"
                value={filters.fromDate || ''}
                onChange={(val) => handleFilterChange('fromDate', val)}
                max={filters.toDate || undefined}
              />
            </Grid>

            {/* To Date */}
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <UiDateField
                label="To Date"
                value={filters.toDate || ''}
                onChange={(val) => handleFilterChange('toDate', val)}
                min={filters.fromDate || undefined}
              />
            </Grid>
            {/* Call Successful Status */}
            
            {/* Call Summary Title */}
           

            {/* Customer Email */}
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter email"
                value={filters.customerEmail || ''}
                onChange={(e) => handleFilterChange('customerEmail', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Grid>

            {/* Search Keyword */}
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                label="Search Query"
                placeholder="Type to search"
                value={filters.searchKeyword || ''}
                onChange={(e) => handleFilterChange('searchKeyword', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Grid>

            {/* Type */}
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Type</InputLabel>
                <Select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  label="Type"
                  sx={{
                    fontSize: '0.875rem',
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>
            </Grid>

             <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <TextField
                fullWidth
                label="Title"
                placeholder="Enter title"
                value={filters.call_summary_title || ''}
                onChange={(e) => handleFilterChange('call_summary_title', e.target.value || null)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                <Select
                  value={filters.call_successful || ''}
                  onChange={(e) => handleFilterChange('call_successful', e.target.value || null)}
                  label="Status"
                  sx={{
                    fontSize: '0.875rem',
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="unknown">Unknown</MenuItem>
                  <MenuItem value="failure">Failure</MenuItem>
                  {/* <MenuItem value="in-progress">In Progress</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>



            {/* Action Buttons */}
            <Grid size={{ xs: 12, sm: 12, md: 2.4 }}>
              <Box sx={{
                display: 'flex',
                gap: 1,
                flexDirection: isMobile ? 'column' : 'row',
                '& > *': {
                  flex: isMobile ? 1 : 'none'
                }
              }}>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={handleFilterSearch}
                  disabled={loading}
                  size="small"
                  fullWidth={isMobile}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleFilterReset}
                  disabled={loading}
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    borderColor: '#6b7280',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#374151',
                      backgroundColor: '#f9fafb',
                    },
                    fontSize: '0.875rem',
                    px: 2
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Conversations Display - Mobile Cards or Desktop Table */}
        {isMobile ? (
          // Mobile Card Layout
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  Loading conversations...
                </Typography>
              </Box>
            ) : conversations.length > 0 ? (
              conversations.map((conversation, index) => (
                <MobileConversationCard
                  key={conversation.conversation_id || `${index}`}
                  conversation={conversation}
                  index={index}
                />
              ))
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ForumIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: '#6b7280', mb: 1 }}>
                    No conversations found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    Conversations will appear here when available
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Mobile Pagination */}
            {conversations.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={pagination.totalCount || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      paddingLeft: 1,
                      paddingRight: 1,
                      flexWrap: 'wrap',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.75rem',
                      color: '#6b7280',
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: '0.75rem',
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // Desktop Table Layout
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1, minWidth: 70, maxWidth: 70, width: 70 }}>S. No</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Conv. ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Messages</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Type</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Credits (Call)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Credits (LLM)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Cost (Call)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Cost (LLM)</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Total Cost</TableCell> */}
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 1 }}>Title</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: 'center', py: 8, height: 400 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <CircularProgress size={40} sx={{ mb: 2 }} />
                          <Typography variant="body1" sx={{ color: '#6b7280' }}>
                            Loading conversations...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : conversations.length > 0 ? (
                    conversations.map((conversation, index) => (
                      <TableRow
                        key={conversation.conversation_id || `${index}`}
                        hover
                        onClick={() => handleRowClick(conversation.conversation_id)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f8fafc' }
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
                            {conversation?.convoId || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {conversation?.customerEmail ? conversation?.customerEmail : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, width: 200 }}>
                            {formatDate(conversation?.metadata?.start_time_unix_secs)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {formatDuration(conversation?.call_duration_secs || conversation?.metadata?.call_duration_secs)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {conversation?.msg || conversation?.message_count || 0}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {conversation?.metadata?.phone_call === null ? 'Text' : 'Call'}
                          </Typography>
                        </TableCell>
                        {/* <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {conversation?.metadata?.charging?.call_charge || 0}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {conversation?.metadata?.charging?.llm_charge || 0}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {`$${(webhookPerCredit * (conversation?.metadata?.charging?.call_charge)).toFixed(3)}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {`$${(conversation?.metadata?.charging?.llm_price || 0).toFixed(3)}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            { `$${((webhookPerCredit * (conversation?.metadata?.charging?.call_charge))+ (conversation?.metadata?.charging?.llm_price)).toFixed(3)}`}
                          </Typography>
                        </TableCell> */}
                        <TableCell sx={{ py: 1 }}>
                          {/* <Chip
                            label={conversation?.analysis?.call_successful}
                            color={getStatusColor(conversation?.analysis?.call_successful)}
                            size="small"
                            variant="outlined"
                          /> */}
                          <Chip
                            label={
                              conversation?.metadata?.termination_reason === "No user message received for a long period of time - probably a network error occurred." ?
                                "success" :
                                conversation?.analysis?.call_successful
                            }
                            color={getStatusColor(
                              conversation?.metadata?.termination_reason === "No user message received for a long period of time - probably a network error occurred." ?
                                "success" :
                                conversation?.analysis?.call_successful
                            )}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, py: 1 }}>
                          <Typography variant="body2" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#6b7280'
                          }}>
                            {conversation.call_summary_title || conversation?.analysis?.call_summary_title || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <ForumIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="body1" sx={{ color: '#6b7280', mb: 1 }}>
                            No conversations found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            Conversations will appear here when available
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Summary Row */}
                  {/* {loading ? null : 
                  conversations.length > 0 && (
                    <TableRow sx={{ 
                      backgroundColor: '#f8fafc',
                      borderTop: '2px solid #e5e7eb',
                      '&:hover': { backgroundColor: '#f8fafc' }
                    }}>
                      <TableCell sx={{ py: 2, fontWeight: 600, color: '#111827' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {conversations.reduce((sum, conv) => 
                            sum + (conv.msg || conv.message_count || 0), 0
                          ).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {conversations.reduce((sum, conv) => 
                            sum + (conv?.metadata?.charging?.call_charge || 0), 0
                          ).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {conversations.reduce((sum, conv) => 
                            sum + (conv?.metadata?.charging?.llm_charge || 0), 0
                          ).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {`$${conversations.reduce((sum, conv) => 
                            sum + (webhookPerCredit * (conv?.metadata?.charging?.call_charge || 0)), 0
                          ).toFixed(3)}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {`$${conversations.reduce((sum, conv) => 
                            sum + (conv?.metadata?.charging?.llm_price || 0), 0
                          ).toFixed(3)}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                          {`$${conversations.reduce((sum, conv) => 
                            sum + ((webhookPerCredit * (conv?.metadata?.charging?.call_charge || 0)) + (conv?.metadata?.charging?.llm_price || 0)), 0
                          ).toFixed(3)}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )} */}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Desktop Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={pagination.totalCount || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #e5e7eb',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                  color: '#6b7280',
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Paper>
        )}

        {/* Summary Section */}
        {loading ? null : (
          summary && (
            <Paper sx={{
              mt: 3,
              p: isMobile ? 2 : 3,
              maxWidth: isMobile ? '100%' : 500,
              // margin: '0 auto',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: 'none',
              backgroundColor: '#ffffff'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{
                fontWeight: 600,
                color: '#111827',
                mb: 2,
                borderBottom: '1px solid #e5e7eb',
                pb: 1
              }}>
                Summary
              </Typography>

              <Box>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Calls
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {summary.total_conversation?.toLocaleString() || 0}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Duration (min)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {summary.total_call_duration_secs ? Math.round(summary.total_call_duration_secs / 60) : 0}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Messages
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {summary.total_messages?.toLocaleString() || 0}
                  </Typography>
                </Box>

                {/* <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Call Credits
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {summary.totalCharges_call?.toLocaleString() || 0}
                  </Typography>
                </Box>
              
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    LLM Credits
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {summary.lmmCredits?.toLocaleString() || 0}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Call Cost
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {`$${(webhookPerCredit * summary.totalCharges_call || 0).toFixed(2)}`}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    LLM Cost
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {`$${(summary?.totalCharges_llm || 0).toFixed(2)}`}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Total Credit
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {
                      (summary.totalCharges_call + summary.lmmCredits).toLocaleString()
                    }
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Total Cost
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {
                      `$${((webhookPerCredit * summary.totalCharges_call) + (summary?.totalCharges_llm)).toFixed(2)}`
                    }
                  </Typography>
                </Box> */}

              </Box>
            </Paper>
          )
        )}
      </Box>

      {/* Conversation Detail Sidebar */}
      <ConversationDetailSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        conversationId={selectedConversationId}
      />

    </>
    // </Layout>
  );
}

export default Conversation;